import type { BookMetadata, OutputChapter } from '../../types';

const mp3Files = import.meta.glob('/audio/*.mp3', { eager: true }) as {
  [url: string]: { default: string };
};
console.assert(Object.keys(mp3Files).length > 0);
/* mp3Files = {
 *   '/audio/AEBWYIN1DA_B01_MAT_001.mp3': {
 *     'default': '/audio/AEBWYIN1DA_B01_MAT_001-BY8Aoyuu.mp3',
 *   },
 * }
 */

export class AudioPlayer {
  audio: HTMLAudioElement;
  currentChapter: OutputChapter | null;
  currentBook: BookMetadata | null;
  cachedAudioFiles: Set<string>;
  playerElement: HTMLDivElement;
  playPauseBtn: HTMLButtonElement;
  audioTitle: HTMLSpanElement;
  progressBar: HTMLDivElement;
  progressFill: HTMLDivElement;
  timeDisplay: HTMLSpanElement;
  closeBtn: HTMLButtonElement;

  constructor() {
    this.audio = new Audio();
    this.currentChapter = null;
    this.currentBook = null;
    this.cachedAudioFiles = new Set();

    this.playerElement = document.getElementById(
      'audio-player',
    ) as HTMLDivElement;
    this.playPauseBtn = document.getElementById(
      'play-pause-btn',
    ) as HTMLButtonElement;
    this.audioTitle = document.getElementById('audio-title') as HTMLSpanElement;
    this.progressBar = document.getElementById(
      'progress-bar',
    ) as HTMLDivElement;
    this.progressFill = document.getElementById(
      'progress-fill',
    ) as HTMLDivElement;
    this.timeDisplay = document.getElementById(
      'time-display',
    ) as HTMLSpanElement;
    this.closeBtn = document.getElementById(
      'close-player-btn',
    ) as HTMLButtonElement;
    this.setupEventListeners();
    this.loadCachedAudioList();
  }

  setupEventListeners() {
    // Play/Pause button
    this.playPauseBtn.addEventListener('click', () => {
      this.togglePlayPause();
    });

    // Close button
    this.closeBtn.addEventListener('click', () => {
      this.hide();
    });

    // Progress bar click
    this.progressBar.addEventListener('click', (e) => {
      const rect = this.progressBar.getBoundingClientRect();
      const dir = window.getComputedStyle(this.progressBar).direction;
      // e.clientX is the number of pixels left edge of the window, regardless
      // of the direction of the element right-to-left or left-to-right.
      let fraction: number;
      if (dir === 'rtl') {
        fraction = (rect.width - (e.clientX - rect.left)) / rect.width;
      } else if (dir === 'ltr') {
        fraction = (e.clientX - rect.left) / rect.width;
      } else {
        throw new Error('Unexpected direction value');
      }
      this.seekToFraction(fraction);
    });

    // Audio events
    this.audio.addEventListener('loadstart', () => {
      this.updatePlayButton();
    });

    this.audio.addEventListener('canplay', () => {
      this.updatePlayButton();
    });

    this.audio.addEventListener('play', () => {
      this.updatePlayButton();

      // Update chapter audio button
      const audioBtn =
        document.querySelector<HTMLButtonElement>('#chapter-audio-btn');
      if (audioBtn) {
        audioBtn.innerHTML = '⏸ إيقاف الصوت';
        audioBtn.disabled = false;
      }
    });

    this.audio.addEventListener('playing', () => {
      this.updatePlayButton();
    });

    this.audio.addEventListener('pause', () => {
      this.updatePlayButton();

      // Update chapter audio button
      const audioBtn =
        document.querySelector<HTMLButtonElement>('#chapter-audio-btn');
      if (audioBtn) {
        audioBtn.innerHTML = '▶ تشغيل الصوت';
        audioBtn.disabled = false;
      }
    });

    this.audio.addEventListener('timeupdate', () => {
      this.updateProgress();
    });

    this.audio.addEventListener('ended', () => {
      this.updatePlayButton();
      this.progressFill.style.width = '0%';
    });

    this.audio.addEventListener('error', (e) => {
      console.error('Audio error:', e);
      this.updatePlayButton();
      this.audioTitle.textContent = 'خطأ في تحميل الصوت';
    });
  }

  isPlaying() {
    return (
      this.audio &&
      !this.audio.paused &&
      !this.audio.ended &&
      this.audio.readyState > 2
    );
  }

  playChapter(book: BookMetadata, chapter: OutputChapter) {
    const paddedAudioBookNumber = String(book.bookNumber - 40).padStart(2, '0');
    const paddedChapterNumber = String(chapter.number).padStart(3, '0');
    const audioFilename = `AEBWYIN1DA_B${paddedAudioBookNumber}_${book.id}_${paddedChapterNumber}.mp3`;
    const audioSrc = mp3Files[`/audio/${audioFilename}`]?.default;
    if (audioSrc === undefined) {
      console.warn('No audio available for this chapter');
      return;
    }

    this.currentBook = book;
    this.currentChapter = chapter;

    // Update UI
    this.audioTitle.textContent = `${book.name} - الإصحاح ${chapter.number}`;
    this.show();

    // Update audio button text if it exists
    const audioBtn =
      document.querySelector<HTMLButtonElement>('#chapter-audio-btn');
    if (audioBtn) {
      audioBtn.innerHTML = '⏳ جاري التحميل...';
      audioBtn.disabled = true;
    }

    // Load and play audio
    this.audio.src = audioSrc;
    this.audio.load();
    this.audio.addEventListener(
      'canplay',
      () => {
        // Try to play (will be blocked if user hasn't interacted yet)
        this.audio.play().catch((error) => {
          console.log('Auto-play blocked:', error);
          // User will need to click play button
          if (audioBtn) {
            audioBtn.innerHTML = '▶ تشغيل الصوت';
            audioBtn.disabled = false;
          }
        });
      },
      { once: true },
    );
  }

  togglePlayPause() {
    if (!this.audio.src) return;

    if (this.isPlaying()) {
      this.audio.pause();
    } else {
      this.audio.play().catch((error) => {
        console.error('Play error:', error);
        this.updatePlayButton();
      });
    }
  }

  seekToFraction(fraction: number) {
    if (!this.audio.duration) return; // metadata hasn't loaded yet
    const newTimeInSeconds = this.audio.duration * fraction;
    this.audio.currentTime = newTimeInSeconds;
  }

  seekToSeconds(seconds: number) {
    if (!this.audio.duration) return; // metadata hasn't loaded yet
    this.audio.currentTime = seconds;
  }

  updateProgress() {
    if (!this.audio.duration) return;

    const percentage = (this.audio.currentTime / this.audio.duration) * 100;
    this.progressFill.style.width = `${percentage}%`;

    const currentTime = this.formatTime(this.audio.currentTime);
    const totalTime = this.formatTime(this.audio.duration);
    this.timeDisplay.textContent = `${currentTime} / ${totalTime}`;
  }

  updatePlayButton() {
    let text: string;
    if (!this.audio || !this.audio.duration) {
      text = '⏳';
    } else if (!this.isPlaying()) {
      text = '▶';
    } else {
      text = '⏸';
    }
    this.playPauseBtn.textContent = text;
  }

  formatTime(seconds: number) {
    if (Number.isNaN(seconds)) return '0:00';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  show() {
    this.playerElement.classList.remove('hidden');
  }

  hide() {
    this.playerElement.classList.add('hidden');
    this.audio.pause();
    this.audio.src = '';
    this.currentChapter = null;
    this.currentBook = null;
  }

  // Check if audio file exists
  async checkAudioExists(audioPath: string) {
    try {
      const response = await fetch(audioPath, { method: 'HEAD' });
      return response.ok;
    } catch (_error) {
      return false;
    }
  }

  async loadCachedAudioList() {
    // Check which audio files are already cached
    if ('caches' in window) {
      try {
        const audioCache = await caches.open('tunisian-bible-audio-v1');
        const cachedRequests = await audioCache.keys();
        cachedRequests.forEach((request) => {
          this.cachedAudioFiles.add(request.url);
        });
        console.log('Loaded cached audio files:', this.cachedAudioFiles.size);
      } catch (error) {
        console.error('Error loading cached audio list:', error);
      }
    }
  }
}
